import * as React from 'react';
import { ActionIcon, Badge, type BadgeProps, createPolymorphicComponent } from '@mantine/core';
import { IconSend } from '@tabler/icons-react';
import { _isString } from '@epihub/core/src/fn/string';
import { type QuickResponse } from '@krishanmadan/core/src/types';
import { useLatestRef } from '@epihub/web/src/hooks/useLatestRef';
import { stopPropagation } from '@epihub/web/src/hooks/useStopPropagation';
import { styled, useTheme } from '../theme/theme';
import { useGlobalStore } from '../store/useGlobalStore';
import { useSendMessage } from '../hooks/useSendMessage';
import { cssBody3 } from './css';
import { type useTextInputFocusHandler } from './chat/useTextInputFocusHandler';

type Props = {
  className?: string;
  setInput: (input: string) => void;
  textInputFocusHandler: ReturnType<typeof useTextInputFocusHandler>;
};

export const QuickResponseChips = ({
  className,
  setInput,
  textInputFocusHandler,
}: Props) => {
  const quickResponses = useGlobalStore((store) => store.settings.quickResponses);
  return (
    <StyledStack className={className}>
      {quickResponses
        .map((quickResponse) => (
          <QuickResponseChip
            key={quickResponse.content}
            quickResponse={quickResponse}
            setInput={setInput}
            textInputFocusHandler={textInputFocusHandler}
          />
        ))}
    </StyledStack>
  );
};

type TQuickResponseChipProps = {
  quickResponse: QuickResponse;
  setInput: (input: string) => void;
  textInputFocusHandler: ReturnType<typeof useTextInputFocusHandler>;
};

export const QuickResponseChip = ({
  quickResponse,
  setInput,
  textInputFocusHandler,
}: TQuickResponseChipProps) => {
  const sendMessage = useSendMessage();
  const sendMessageRef = useLatestRef(sendMessage);

  const onActionSend = React.useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    stopPropagation(event);
    sendMessageRef.current(quickResponse.content, {
      x: event.clientX,
      y: event.clientY,
    });
    setInput('');
  }, [sendMessageRef, quickResponse.content]);

  // For now we'll give this all the same behaviour
  const onClick = onActionSend;

  const onItemPress = textInputFocusHandler.refocusHoc(onClick);
  const onSendPress = textInputFocusHandler.refocusHoc(onActionSend);
  const theme = useTheme();
  return (
    <StyledChip
      key={quickResponse.content}
      color={theme.v0.col.success.hex}
      variant='outline'
      {...onItemPress}
      h={36}
      leftSection={_isString(quickResponse.emoji)
        ? (
          <StyledIcon>
            {quickResponse.emoji}
          </StyledIcon>
        )
        : undefined}
    >
      <Label>
        <LabelText>{quickResponse.content}</LabelText>
        <StyledIconButton
          aria-label={`send-${quickResponse.content}`.toLowerCase()}
          color='green'
          variant='transparent'
          {...onSendPress as any}
        >
          <IconSend />
        </StyledIconButton>
      </Label>
    </StyledChip>
  );
};

const StyledStack = styled.div`
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  gap: 8px;
  margin: 24px 0;

  // Mobile: horizontal scrollable row with fade indicator
  @media (max-width: 768px) {
    flex-direction: row;
    flex-wrap: nowrap;
    gap: 8px;
    margin: 8px 0;
    overflow-x: auto;
    overflow-y: hidden;
    padding-right: 24px;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    -ms-overflow-style: none;

    &::-webkit-scrollbar {
      display: none;
    }

    // Fade indicator at the end
    mask-image: linear-gradient(
      to right,
      black 0%,
      black 85%,
      transparent 100%
    );
    -webkit-mask-image: linear-gradient(
      to right,
      black 0%,
      black 85%,
      transparent 100%
    );
  }

  @media (min-width: 769px) {
    flex-direction: row;
    gap: 16px;
  }
`;

const StyledChip = createPolymorphicComponent<'div', BadgeProps>(styled(Badge)`
  ${cssBody3};
  cursor: pointer;
  color: ${({ theme }) => theme.legacy.colors.primaryText};
  font-style: italic;
  text-transform: none;
  overflow-wrap: break-word;
  text-overflow: clip;
  white-space: normal;
  word-wrap: break-word;

  .mantine-Badge-label {
    text-transform: none;
  }

  // Mobile: smaller pills with larger text
  @media (max-width: 768px) {
    height: 28px !important;
    padding: 0 10px;
    font-size: 14px;
    flex-shrink: 0; // Prevent shrinking in horizontal scroll

    .mantine-Badge-label {
      font-size: 14px;
    }
  }
`);

const Label = styled.div`
  align-items: center;
  display: flex;
  flex-direction: row;
`;

const LabelText = styled.div`

`;

const StyledIconButton = styled(ActionIcon)`
  align-items: center;
  display: flex;
  justify-content: center;
  margin-left: 12px;
`;

const StyledIcon = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
  width: 26px;
`;
